import React, { useEffect, useRef, useState } from "react";
import { ProductItemInterface } from "../../../assets/interfaces";
import CartContext from "../../../contexts/cart-context";
import LazyImage from "@/components/lazy-image";
import AddToCartButton from "./add-to-cart-btn";
import { DiscountBadge, DiscountBadgeImage, OptionsBadge } from "./bages";
import { getDiscountPercentage, getFormattedDate, getProductOptionsLength } from "@/assets/js/utils/utils";
import classNames from "classnames";
import { capitalizeFirstLetter } from "@/assets/js/utils/functions";
import { AppBtn } from "@/components/ui/buttons";
import CartContextV2 from "@/contexts/cart-context-v2";

interface ProductItemProps {
  openItem: () => void;
  index: number;
  item: ProductItemInterface;
  showAddToCart?: boolean;
}

const ProductItem: React.FC<ProductItemProps> = ({ openItem, index, item, showAddToCart = true }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewTimeout = useRef<NodeJS.Timeout | null>(null);

  const discountExists = item.discount_price !== undefined && item.discount_price > 0;
  const { addOrRemoveItem, setCurrentProduct, isItemInCart, formatAsCurrency, cart, toggleModal } =
    CartContextV2.useContainer();
  const [isDropUpOpen, setIsDropUpOpen] = useState<Boolean>(false);
  const [qty, setQty] = useState<number>(1);
  const [itemError, setItemError] = useState<string | null>(null);
  const dropUpRef = useRef<HTMLDivElement>(null);

  console.log({ cart });

  useEffect(() => {
    const cartItem = cart.find((cartItem) => cartItem.id === item.id && !cartItem.variant);
    if (cartItem) {
      setQty(cartItem.quantity);
    }
  }, [cart, item.id]);

  useEffect(() => {
    const handleScroll = () => {
      if (isDropUpOpen) {
        setIsDropUpOpen(false); // Close the dropdown on scroll
      }
    };

    document.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("scroll", handleScroll); // Cleanup on unmount
    };
  }, [isDropUpOpen]);

  const handleItemButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    console.log("CLICK");
    e.stopPropagation();
    const res = addOrRemoveItem(item, [], [qty]);

    if (res?.errors) {
      setItemError(typeof res.errors === "string" ? res.errors : Object.values(res.errors)[0]);
    } else {
      setItemError(null);
      setIsDropUpOpen(false);
    }
  };

  const handleToggleDropUp = (e) => {
    console.log("Toggled", isDropUpOpen);
    e.stopPropagation();
    var variants = item?.variants?.options.filter((i) => i.id).map((i) => i.id);
    const itemHasVariants = item?.variants?.options.length > 0;

    if (variants && variants?.length > 0) {
      setCurrentProduct(item);
      toggleModal("variants");
    } else {
      if (qty === 0) {
        setQty(1);
      }
      setIsDropUpOpen(!isDropUpOpen);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const addToCartButton = document.querySelector(`.add-to-cart-toggle-${item.id}`);
    if (
      event.target instanceof Node &&
      !addToCartButton.contains(event.target) &&
      dropUpRef.current &&
      !dropUpRef.current.contains(event.target as Node)
    ) {
      setIsDropUpOpen(false);
    }
  };

  useEffect(() => {
    if (isDropUpOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropUpOpen]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let touchTimeout;
    let isLongPress = false;

    // For desktop: mouse hover events
    const handleMouseEnter = () => {
      video.currentTime = 0;
      video.play();
      previewTimeout.current = setInterval(() => {
        if (video.currentTime >= 3) {
          video.currentTime = 0;
          video.play(); // restart playback
        }
      }, 100); // check every 100ms
    };

    const handleMouseLeave = () => {
      clearInterval(previewTimeout.current);
      video.pause();
      video.currentTime = 0;
    };

    // For mobile: touch events for long press
    const handleTouchStart = (e) => {
      touchTimeout = setTimeout(() => {
        isLongPress = true;
        video.currentTime = 0;
        video.play();
        previewTimeout.current = setInterval(() => {
          if (video.currentTime >= 3) {
            video.currentTime = 0;
            video.play(); // restart playback
          }
        }, 100);
      }, 500); // 500ms for long press
    };

    const handleTouchEnd = (e) => {
      clearTimeout(touchTimeout);

      // Only prevent click if this was a long press
      if (isLongPress) {
        e.preventDefault();
        clearInterval(previewTimeout.current);
        video.pause();
        video.currentTime = 0;
        isLongPress = false;
      }
    };

    const handleTouchMove = (e) => {
      // Cancel long press if user moves finger
      clearTimeout(touchTimeout);
      if (isLongPress) {
        clearInterval(previewTimeout.current);
        video.pause();
        video.currentTime = 0;
        isLongPress = false;
      }
    };

    // Prevent click events if it was a long press
    const handleClick = (e) => {
      if (isLongPress) {
        e.preventDefault();
        e.stopPropagation();
        isLongPress = false;
        return false;
      }
    };

    // Add all event listeners
    video.addEventListener("mouseenter", handleMouseEnter);
    video.addEventListener("mouseleave", handleMouseLeave);
    video.addEventListener("touchstart", handleTouchStart);
    video.addEventListener("touchend", handleTouchEnd);
    video.addEventListener("touchmove", handleTouchMove);
    video.addEventListener("click", handleClick, true);

    // Clean up
    return () => {
      video.removeEventListener("mouseenter", handleMouseEnter);
      video.removeEventListener("mouseleave", handleMouseLeave);
      video.removeEventListener("touchstart", handleTouchStart);
      video.removeEventListener("touchend", handleTouchEnd);
      video.removeEventListener("touchmove", handleTouchMove);
      video.removeEventListener("click", handleClick, true);
      clearInterval(previewTimeout.current);
      clearTimeout(touchTimeout);
    };
  }, [videoRef]);

  return (
    <article key={index} className="w-full flex flex-col cursor-pointer md:relative max-w-[300px]" onClick={openItem}>
      <figure className="w-full rounded-15 overflow-hidden relative flex-shrink-0" style={{ paddingTop: "100%" }}>
        {item.thumbnail_type === "video" && item.videos?.[item.thumbnail]?.thumbnail ? (
          <div className="w-full h-full absolute top-0 left-0 rounded-15 overflow-hidden">
            <div className="absolute bg-grey-loader w-full h-full top-0 left-0 animate-pulse z-[-1]"></div>
            <video
              id="previewVideo"
              src={`${item.videos?.[item.thumbnail]?.url}#t=0.1`}
              poster={item.videos?.[item.thumbnail]?.thumbnail}
              muted
              preload="metadata"
              className="w-full h-full object-cover rounded-15 z-[10]"
              ref={videoRef}
              playsInline
            ></video>
          </div>
        ) : (
          <LazyImage
            src={item.images[item?.thumbnail]}
            alt={item.name}
            loaderClasses="rounded-15"
            className="h-full w-full object-cover rounded-15 absolute top-0 left-0"
          />
        )}
        <div className="absolute top-2.5 left-2.5 flex gap-2 items-start">
          {discountExists && <DiscountBadgeImage percentage={getDiscountPercentage(item)} className="" mode="dark" />}
        </div>

        {!item?.available_to_purchase && (
          <span className="block w-max rounded-md text-accent-red-500 text-xxxs sm:text-xs bg-grey-fields-100 px-2 py-1 absolute top-2.5 left-2.5 font-semibold">
            Unavailable
          </span>
        )}

        {item?.videos?.length > 0 && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-30 text-white flex items-center blur-5 rounded-30 group">
            <span className="block font-semibold text-xxs group-hover:-mr-1 group-hover:opacity-100 opacity-0 transition-all duration-300 w-0 group-hover:w-auto group-hover:pl-2 whitespace-nowrap">
              <span className="group-hover:inline-block hidden">
                {item?.videos?.length} video{item?.videos?.length > 1 ? "s" : ""}
              </span>
            </span>
            <div className="h-8 w-8 rounded-full flex items-center justify-center">
              {/* prettier-ignore */}
              <svg width="45%" viewBox="0 0 24 24" fill="none">
              <path d="M14.7295 2H9.26953V6.36H14.7295V2Z" fill="currentColor"/>
              <path d="M16.2305 2V6.36H21.8705C21.3605 3.61 19.3305 2.01 16.2305 2Z" fill="currentColor"/>
              <path d="M2 7.85938V16.1894C2 19.8294 4.17 21.9994 7.81 21.9994H16.19C19.83 21.9994 22 19.8294 22 16.1894V7.85938H2ZM14.44 16.1794L12.36 17.3794C11.92 17.6294 11.49 17.7594 11.09 17.7594C10.79 17.7594 10.52 17.6894 10.27 17.5494C9.69 17.2194 9.37 16.5394 9.37 15.6594V13.2594C9.37 12.3794 9.69 11.6994 10.27 11.3694C10.85 11.0294 11.59 11.0894 12.36 11.5394L14.44 12.7394C15.21 13.1794 15.63 13.7994 15.63 14.4694C15.63 15.1394 15.2 15.7294 14.44 16.1794Z" fill="currentColor"/>
              <path d="M7.76891 2C4.66891 2.01 2.63891 3.61 2.12891 6.36H7.76891V2Z" fill="currentColor"/>
            </svg>
            </div>
          </div>
        )}
      </figure>
      {isDropUpOpen && (
        <div
          ref={dropUpRef}
          onClick={(e) => e.stopPropagation()}
          className={`fixed md:absolute w-full left-0 right-0 max-sm:bottom-0 z-[999999] md:top-[47%] mt-18 md:mt-25 px-5 py-3 md:px-3 md:p-3 bg-white border border-[#E5E5E580] transform transition ease-out duration-300 rounded-none md:rounded-10 shadow-card flex flex-col gap-y-2 ${
            isDropUpOpen ? "opacity-1 translate-y-0" : "pointer-events-none opacity-0 translate-y-4"
          }`}
        >
          <div className="flex w-full justify-between items-center bg-white pb-2 px-0">
            <dt className="text-1xs sm:text-sm  text-black">Quantity</dt>
            <dd className={`no-outline inline-flex items-center rounded-full flex-shrink-0`}>
              <div className="flex items-center space-x-0.5 md:space-x-2.5">
                <button
                  className="flex-shrink-0 h-7 w-7 sm:h-8 sm:w-8 rounded-full border border-grey-border border-opacity-50 inline-flex items-center justify-center text-placeholder disabled:cursor-not-allowed"
                  // disabled={qty < 2}
                  onClick={() => setQty((prev) => prev - 1)}
                >
                  {/* prettier-ignore */}
                  <svg className="w-[60%]" viewBox="0 0 18 18" fill="none">
                    <path d="M4.5 9H13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                <div className="flex items-stretch">
                  <input
                    type="number"
                    className=" w-8 block h-7.5 sm:w-8 sm:h-8 rounded-5 border-grey-divider focus:border-primary-500 border no-outline text-center  font-semibold text-xxxs sm:text-xs text-dark"
                    value={qty}
                    onChange={(e) => setQty(Number(e.target.value))}
                    min={0}
                    max={item.quantity}
                  />
                </div>
                <button
                  onClick={() => setQty((prev) => prev + 1)}
                  className="flex-shrink-0 h-7 w-7 sm:h-8 sm:w-8 rounded-full border  border-grey-border border-opacity-50 inline-flex items-center justify-center text-placeholder"
                >
                  {/* prettier-ignore */}
                  <svg className="w-[60%]" viewBox="0 0 18 18" fill="none">
                  <path d="M4.5 9H13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 13.5V4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                </button>
              </div>
            </dd>
          </div>

          {itemError && <span className="text-xs font-medium text-accent-red-500">{itemError}</span>}

          <AppBtn isBlock onClick={handleItemButtonClick}>
            {isItemInCart(item.id) ? "Update Cart" : "Add to cart"}
          </AppBtn>
        </div>
      )}
      <div className={`flex w-full items-start justify-between mt-1 relative`}>
        <div className="mt-2.5 flex flex-col justify-start flex-1 gap-y-0.5 overflow-x-hidden">
          <div className={classNames("w-full overflow-hidden flex items-center")}>
            <h5
              className={`text-dark font-medium overflow-hidden text-1sm max-w-full whitespace-nowrap overflow-ellipsis mr-2 font-body  ${
                item.available_to_purchase ? "text-opacity-100" : "text-opacity-50"
              }`}
            >
              {capitalizeFirstLetter(item.name)}
            </h5>
          </div>
          {/** break btw price and title */}
          <div
            className={classNames("text-1xs sm:text-sm text-grey-subtext", {
              "text-opacity-100": item.available_to_purchase,
              "text-opacity-70 font-medium": !item.available_to_purchase,
              "flex items-center": !discountExists,
            })}
          >
            {discountExists && item.available_to_purchase && (
              <div className="flex items-center flex-wrap">
                <div
                  className={`text-black font-display font-bold text-sm mr-1.5 tracking-tighter ${
                    item.available_to_purchase ? "text-opacity-100" : "text-opacity-70"
                  }`}
                  style={{ letterSpacing: "0 !important" }}
                >
                  {formatAsCurrency(item.discount_price)}
                </div>
              </div>
            )}
            <div
              className={classNames(
                {
                  "font-display font-bold line-through text-xxs sm:text-xs text-black-placeholder": discountExists,
                  "font-display font-bold text-sm text-black": !discountExists,
                  // "text-black": item.available_to_purchase,
                  "!text-black-placeholder": !item.available_to_purchase,
                },
                "tracking-tighter"
              )}
            >
              {formatAsCurrency(item.price)}
            </div>
          </div>
        </div>
        {item.available_to_purchase && showAddToCart && (
          <div onClick={(e) => e.stopPropagation()}>
            <AddToCartButton
              isInCart={isItemInCart(item.id)}
              toggle={handleToggleDropUp}
              className={`mt-2.5 add-to-cart-toggle-${item.id}`}
            />
          </div>
        )}
      </div>
      {item?.available_to_purchase && item?.variants?.options?.length > 0 && (
        <div className="inline-flex items-center gap-x-1 mt-2.5">
          <OptionsBadge options={getProductOptionsLength(item?.variants)} className="" />
        </div>
      )}
    </article>
  );
};
export default ProductItem;
